import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import classNames from "classnames";
import { CSVLink } from "react-csv";

/**
 * A reusable dialog component that allows the processing and saving of CSV files.
 * Shows a loading state until queried data is ready. Makes use of CSVLink.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {() => void} props.onClose - Handler for dialog close action
 * @param {string} props.headline - Dialog title text
 * @param {string} props.message - Dialog content message
 * @param {boolean} props.isLoading - Controls loading state of the action button
 * @param {string} props.actionButtonTestId - Test ID for the action button
 * @param {any} props.data - Data to be processed into a CSV
 * @param {string[]} props.columnHeaders - Array of the names of each column in the CSV
 * @param {(data: any) => any} props.generateColumnData - Function to process column data into an acceptable state.
 * @param {string} fileTitle - Name for the resulting CSV file - timestamp will be appended to the end
 */

interface QuickActionDialogProps {
  open: boolean;
  onClose: () => void;
  headline: string;
  message: string;
  columnHeaders: string[];
  fileTitle: string;
  data: any;
  isLoading: boolean;
  error?: Error | null;
  generateColumnData: (data: any, includeReviews: boolean) => any;
  actionButtonTestId: string;
}

export default function QuickActionDialog({
  open,
  onClose,
  headline,
  message,
  columnHeaders,
  fileTitle,
  data,
  isLoading,
  error,
  generateColumnData,
  actionButtonTestId
}: QuickActionDialogProps) {
  const timestamp = Date.now();
  const hasData = data && Array.isArray(data) && data.length > 0;

  const hasError = !!error;

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline={headline}
      className="sm:max-w-[423px] p-6 pr-2"
      actions={
        <>
          <Button variant="text" onClick={onClose}>
            Close
          </Button>
          {isLoading ? (
            <Button
              icon="progress_activity"
              disabled={true}
              iconClassName={classNames("animate-spin w-6 h-6 !block")}
              testId={actionButtonTestId}
              variant="text"
            >
              Preparing...
            </Button>
          ) : hasError ? (
            <Button icon="error" disabled={true} testId={actionButtonTestId} variant="text">
              Download Unavailable
            </Button>
          ) : !hasData ? (
            <Button icon="warning" disabled={true} testId={actionButtonTestId} variant="text">
              No Data Available
            </Button>
          ) : (
            <CSVLink
              data={[columnHeaders.slice(0, 5), ...generateColumnData(data || [], false)]}
              filename={`${fileTitle}-${timestamp}.csv`}
            >
              <Button icon="download" testId={actionButtonTestId} variant="text">
                Download
              </Button>
            </CSVLink>
          )}
        </>
      }
    >
      {hasError ? (
        <p className="text-sys-error">
          Unable to load data. This may be due to a timeout or network issue. Please try again later.
        </p>
      ) : !hasData && !isLoading ? (
        <p>No data available to download.</p>
      ) : (
        <p>{message}</p>
      )}
    </Dialog>
  );
}
